import type { Flashcard, Lesson, Question, Skill, Topic } from '@/data/types';
import { card, choice, example, f, numeric, p, tip, warn } from '../../authoring';
import { TRIG_QUESTIONS, TRIG_TOPIC } from '../trygonometria';

/**
 * Dział 10: Trygonometria.
 *
 * Włącza do kursu trzy umiejętności z pierwszego wycinka treści (wartości,
 * tożsamości, równania) - z ich identyfikatorami i zadaniami - i dopisuje kąt
 * rozwarty, pole trójkąta, twierdzenia sinusów i cosinusów, miarę łukową
 * z wykresami oraz wzory na funkcje sumy i podwojonego kąta.
 */

const r = String.raw;

export const TRIG_COURSE_TOPIC: Topic = {
  ...TRIG_TOPIC,
  summary:
    'Funkcje kąta ostrego i rozwartego, tożsamości, pole trójkąta, twierdzenia sinusów i cosinusów, radiany, wykresy, wzory i równania.',
};

export const TRIG_COURSE_SKILLS: Skill[] = [
  {
    id: 'trig-values',
    topicId: 'math-trigonometry',
    name: 'Funkcje trygonometryczne kąta ostrego',
    level: 'PP',
    ckeRequirement: 'Trygonometria — sinus, cosinus i tangens kąta ostrego w trójkącie prostokątnym',
    prerequisites: ['num-roots'],
    examValue: 0.7,
  },
  {
    id: 'trig-identities',
    topicId: 'math-trigonometry',
    name: 'Tożsamości trygonometryczne',
    level: 'PP',
    ckeRequirement: 'Trygonometria — jedynka trygonometryczna i związek tangensa z sinusem i cosinusem',
    prerequisites: ['trig-values', 'alg-expand'],
    examValue: 0.75,
  },
  {
    id: 'trig-obtuse',
    topicId: 'math-trigonometry',
    name: 'Kąty od 0° do 180° i kąt nachylenia prostej',
    level: 'PP',
    ckeRequirement: 'Trygonometria — funkcje kątów od 0° do 180°, wzory redukcyjne, kąt nachylenia prostej',
    prerequisites: ['trig-identities', 'lin-formula'],
    examValue: 0.6,
  },
  {
    id: 'trig-area',
    topicId: 'math-trigonometry',
    name: 'Trygonometria w figurach: pole z sinusem',
    level: 'PP',
    ckeRequirement: 'Trygonometria — pole trójkąta i równoległoboku ze wzoru z sinusem kąta',
    prerequisites: ['trig-obtuse'],
    examValue: 0.65,
  },
  {
    id: 'trig-laws',
    topicId: 'math-trigonometry',
    name: 'Twierdzenie cosinusów i sinusów',
    // VII.3 (zakres podstawowy): twierdzenie cosinusów; twierdzenie sinusów to VII.R7.
    level: 'PP',
    ckeRequirement: 'Trygonometria — twierdzenie cosinusów (PP) i twierdzenie sinusów (PR)',
    prerequisites: ['trig-area'],
    examValue: 0.7,
  },
  {
    id: 'trig-radians',
    topicId: 'math-trigonometry',
    name: 'Miara łukowa i wykresy funkcji trygonometrycznych',
    level: 'PR',
    ckeRequirement: 'Trygonometria — miara łukowa, funkcje dowolnego kąta, wykresy i okresowość',
    prerequisites: ['trig-obtuse', 'fn-transform'],
    examValue: 0.55,
  },
  {
    id: 'trig-formulas',
    topicId: 'math-trigonometry',
    name: 'Wzory na funkcje sumy i podwojonego kąta',
    level: 'PR',
    ckeRequirement: 'Trygonometria — sinus i cosinus sumy, różnicy i podwojonego kąta',
    prerequisites: ['trig-radians'],
    examValue: 0.6,
  },
  {
    id: 'trig-equations',
    topicId: 'math-trigonometry',
    name: 'Równania trygonometryczne',
    level: 'PR',
    ckeRequirement: 'Trygonometria — równania trygonometryczne',
    prerequisites: ['trig-formulas', 'quad-discriminant'],
    examValue: 0.7,
  },
];

const PI = Math.PI;
const rad = (deg: number) => (deg * PI) / 180;
const wave = (x: number) => 3 * Math.sin(x) + 2;

// ===========================================================================
// Lekcje
// ===========================================================================

export const TRIG_LESSONS: Lesson[] = [
  {
    skillId: 'trig-values',
    minutes: 12,
    intro:
      'Trygonometria zaczyna się od prostego spostrzeżenia: w trójkątach prostokątnych o tym samym kącie ostrym stosunki boków są takie same, niezależnie od wielkości trójkąta. Te stosunki mają nazwy — sinus, cosinus, tangens.',
    blocks: [
      {
        kind: 'figure',
        figure: {
          kind: 'geometry',
          alt: 'Trójkąt prostokątny ABC z kątem prostym przy B. Kąt ostry α przy A. Bok BC naprzeciw α oznaczony a, bok AB przy α oznaczony b, przeciwprostokątna AC oznaczona c.',
          points: { A: [0, 0], B: [4, 0], C: [4, 3] },
          polygons: [{ vertices: ['A', 'B', 'C'] }],
          segments: [
            { from: 'A', to: 'B', label: 'b' },
            { from: 'B', to: 'C', label: 'a' },
            { from: 'A', to: 'C', label: 'c' },
          ],
          angles: [
            { at: 'A', from: 'B', to: 'C', label: 'α' },
            { at: 'B', from: 'A', to: 'C', right: true },
          ],
        },
        caption: 'a — naprzeciw kąta α, b — przy kącie α, c — przeciwprostokątna.',
      },
      f(r`\sin\alpha = \frac{a}{c} \qquad \cos\alpha = \frac{b}{c} \qquad \mathrm{tg}\,\alpha = \frac{a}{b}`),
      p(r`Wartości dla $30^\circ$, $45^\circ$ i $60^\circ$ są w tablicach CKE, ale warto je znać na pamięć: $\sin 30^\circ = \frac12$, $\sin 45^\circ = \frac{\sqrt2}{2}$, $\sin 60^\circ = \frac{\sqrt3}{2}$ — a cosinusy to te same liczby w odwrotnej kolejności.`),
      tip(r`Sinus i cosinus kąta ostrego leżą między $0$ a $1$ — przyprostokątna jest krótsza od przeciwprostokątnej. Wynik $\sin\alpha = 1{,}2$ oznacza pomyłkę.`),
      warn('Zawsze sprawdź, który bok leży naprzeciw kąta, a który przy nim. Zamiana daje sinus zamiast cosinusa.'),
    ],
    examples: [
      example(
        r`Przyprostokątne mają długości $6$ i $8$. Oblicz cosinus kąta leżącego naprzeciw boku $6$.`,
        [r`Przeciwprostokątna: $\sqrt{36 + 64} = 10$.`, [r`$\cos\alpha = \frac{8}{10} = 0{,}8$`, 'bo bok 8 leży przy tym kącie.']],
        r`$0{,}8$`,
      ),
      example(
        r`Drabina długości $4$ m tworzy z podłożem kąt $60^\circ$. Jak wysoko sięga?`,
        [r`Wysokość leży naprzeciw kąta: $h = 4 \sin 60^\circ$.`, r`$h = 4 \cdot \frac{\sqrt3}{2} = 2\sqrt3 \approx 3{,}46$ m.`],
        r`$2\sqrt3$ m`,
      ),
    ],
    pitfalls: ['Pomylony bok przyległy z przeciwległym.', 'Sinus większy od 1 przyjęty bez zastanowienia.', r`Wartości $\sin 30^\circ$ i $\sin 60^\circ$ zamienione miejscami.`],
  },
  {
    skillId: 'trig-identities',
    minutes: 12,
    intro:
      'Z twierdzenia Pitagorasa wynika najważniejszy wzór trygonometrii — jedynka trygonometryczna. Dzięki niej z jednej funkcji kąta ostrego wyliczysz wszystkie pozostałe.',
    blocks: [
      f(r`\sin^2\alpha + \cos^2\alpha = 1 \qquad \mathrm{tg}\,\alpha = \frac{\sin\alpha}{\cos\alpha}`),
      f(r`\sin(90^\circ - \alpha) = \cos\alpha \qquad \mathrm{tg}\,(90^\circ - \alpha) = \frac{1}{\mathrm{tg}\,\alpha}`),
      p(r`Przepis: znasz $\sin\alpha$ — liczysz $\cos\alpha = \sqrt{1 - \sin^2\alpha}$ (dla kąta ostrego dodatni), potem $\mathrm{tg}\,\alpha$ jako iloraz.`),
      tip(r`Gdy znasz $\mathrm{tg}\,\alpha$, zapisz $\sin\alpha = \mathrm{tg}\,\alpha \cdot \cos\alpha$ i wstaw do jedynki. Albo narysuj trójkąt: $\mathrm{tg}\,\alpha = 2$ to przyprostokątne $2$ i $1$.`),
      warn(r`$\sin^2\alpha$ to $(\sin\alpha)^2$, a nie $\sin(\alpha^2)$.`),
    ],
    examples: [
      example(
        r`Kąt $\alpha$ jest ostry, $\sin\alpha = \frac35$. Oblicz $\mathrm{tg}\,\alpha$.`,
        [r`$\cos\alpha = \sqrt{1 - \frac{9}{25}} = \frac45$.`, r`$\mathrm{tg}\,\alpha = \frac{3/5}{4/5} = \frac34$.`],
        r`$\frac34$`,
      ),
      example(
        r`Kąt $\alpha$ jest ostry, $\mathrm{tg}\,\alpha = 3$. Oblicz $\frac{\sin\alpha + \cos\alpha}{\sin\alpha - \cos\alpha}$.`,
        [r`Dzielę licznik i mianownik przez $\cos\alpha$: $\frac{\mathrm{tg}\,\alpha + 1}{\mathrm{tg}\,\alpha - 1}$.`, r`$= \frac{4}{2} = 2$.`],
        r`$2$`,
      ),
    ],
    pitfalls: [r`$\cos\alpha = 1 - \sin\alpha$ zamiast pierwiastka z $1 - \sin^2\alpha$.`, 'Zapomniany pierwiastek po jedynce trygonometrycznej.', 'Tangens odwrócony (cos/sin).'],
  },
  {
    skillId: 'trig-obtuse',
    minutes: 12,
    intro:
      'Kąt rozwarty nie mieści się w trójkącie prostokątnym, więc sinus i cosinus definiuje się przez punkt na okręgu jednostkowym: cosinus to współrzędna x, sinus — współrzędna y.',
    blocks: [
      {
        kind: 'figure',
        figure: {
          kind: 'geometry',
          alt: 'Okrąg jednostkowy z osiami. Punkty dla kątów 30° i 150° leżą na tej samej wysokości — mają ten sam sinus, a przeciwne cosinusy.',
          points: {
            O: [0, 0],
            X: [1.25, 0],
            Xm: [-1.25, 0],
            Y: [0, 1.25],
            Ym: [0, -1.25],
            P: [Math.cos(rad(150)), Math.sin(rad(150))],
            Q: [Math.cos(rad(30)), Math.sin(rad(30))],
          },
          circles: [{ center: 'O', radius: 1 }],
          segments: [
            { from: 'Xm', to: 'X' },
            { from: 'Ym', to: 'Y' },
            { from: 'O', to: 'P' },
            { from: 'O', to: 'Q' },
            { from: 'P', to: 'Q', dashed: true },
          ],
          angles: [{ at: 'O', from: 'X', to: 'P', label: '150°' }],
          hidePointLabels: true,
        },
        caption: 'Punkty dla 30° i 150°: ta sama wysokość (sinus), przeciwne x (cosinus).',
      },
      f(r`\sin(180^\circ - \alpha) = \sin\alpha \qquad \cos(180^\circ - \alpha) = -\cos\alpha`),
      p(r`Dla kąta rozwartego sinus jest dodatni, a cosinus i tangens — ujemne. Np. $\cos 120^\circ = -\cos 60^\circ = -\frac12$.`),
      f(r`y = ax + b: \quad \mathrm{tg}\,\alpha = a`, 'α — kąt nachylenia prostej do osi x'),
      warn(r`Z samego $\sin\alpha = \frac12$ nie wiesz, czy $\alpha = 30^\circ$, czy $150^\circ$. Szukaj w treści informacji, czy kąt jest ostry.`),
    ],
    examples: [
      example(
        r`Oblicz $\cos 135^\circ$.`,
        [r`$\cos 135^\circ = \cos(180^\circ - 45^\circ) = -\cos 45^\circ$.`, r`$= -\frac{\sqrt2}{2}$.`],
        r`$-\frac{\sqrt2}{2}$`,
      ),
      example(
        r`Pod jakim kątem prosta $y = -x + 3$ jest nachylona do osi $x$?`,
        [r`$\mathrm{tg}\,\alpha = -1$.`, r`Tangens ujemny, więc kąt rozwarty: $\alpha = 135^\circ$.`],
        r`$135^\circ$`,
      ),
    ],
    pitfalls: ['Dodatni cosinus kąta rozwartego.', 'Kąt nachylenia wzięty ostry, choć współczynnik jest ujemny.', r`$\sin 150^\circ$ policzony jako $-\frac12$.`],
  },
  {
    skillId: 'trig-area',
    minutes: 10,
    intro:
      'Wysokość trójkąta często jest ukryta. Wzór z sinusem pozwala policzyć pole z dwóch boków i kąta między nimi — bez szukania wysokości.',
    blocks: [
      f(r`P = \frac12 \, a b \sin\gamma`, 'γ — kąt między bokami a i b'),
      f(r`P_{\text{równoległoboku}} = a b \sin\alpha`),
      p(r`Skąd wzór: wysokość opuszczona na bok $a$ ma długość $b\sin\gamma$, a pole to $\frac12 \cdot a \cdot h$.`),
      tip(r`$\sin(180^\circ - \alpha) = \sin\alpha$, więc kąt ostry i rozwarty, które sumują się do $180^\circ$, dają to samo pole. Romb o kącie $150^\circ$ ma to samo pole co romb o kącie $30^\circ$.`),
      warn('Kąt we wzorze musi leżeć MIĘDZY bokami, które mnożysz.'),
    ],
    examples: [
      example(
        r`Boki trójkąta mają długości $6$ i $8$, a kąt między nimi $30^\circ$. Oblicz pole.`,
        [r`$P = \frac12 \cdot 6 \cdot 8 \cdot \sin 30^\circ$.`, r`$= 24 \cdot \frac12 = 12$.`],
        r`$12$`,
      ),
      example(
        r`Pole trójkąta o bokach $5$ i $12$ wynosi $15$, a kąt między nimi jest rozwarty. Jaki to kąt?`,
        [r`$15 = \frac12 \cdot 5 \cdot 12 \cdot \sin\gamma \Rightarrow \sin\gamma = \frac12$.`, r`Kąt rozwarty: $\gamma = 150^\circ$.`],
        r`$150^\circ$`,
      ),
    ],
    pitfalls: ['Pominięte ½ we wzorze na pole trójkąta.', 'Kąt nieleżący między mnożonymi bokami.', 'Ze sinusa wybrany kąt ostry, choć treść mówi o rozwartym.'],
  },
  {
    skillId: 'trig-laws',
    minutes: 14,
    intro:
      'Twierdzenia sinusów i cosinusów to narzędzia do „rozwiązywania” dowolnego trójkąta — nie tylko prostokątnego. Znając trzy elementy (boki i kąty), wyliczasz resztę.',
    blocks: [
      f(r`c^2 = a^2 + b^2 - 2ab\cos\gamma`, 'twierdzenie cosinusów'),
      f(r`\frac{a}{\sin\alpha} = \frac{b}{\sin\beta} = \frac{c}{\sin\gamma} = 2R`, 'twierdzenie sinusów, R — promień okręgu opisanego'),
      p('Cosinusów używasz, gdy znasz dwa boki i kąt między nimi albo trzy boki. Sinusów — gdy znasz bok i kąt naprzeciw niego oraz jeszcze jeden element.'),
      tip(r`Dla $\gamma = 90^\circ$ twierdzenie cosinusów to Pitagoras. Znak: jeśli $c^2 > a^2 + b^2$, kąt $\gamma$ jest rozwarty.`),
      warn(r`$\cos 120^\circ = -\frac12$, więc $-2ab\cos 120^\circ = +ab$. Zgubiony minus to najczęstszy błąd w tym temacie.`),
    ],
    examples: [
      example(
        r`Boki $30$ i $50$, kąt między nimi $120^\circ$. Oblicz trzeci bok.`,
        [r`$c^2 = 900 + 2500 - 2 \cdot 30 \cdot 50 \cdot \left(-\frac12\right)$.`, r`$= 3400 + 1500 = 4900$, więc $c = 70$.`],
        r`$70$`,
      ),
      example(
        r`W trójkącie $a = 6$, $\alpha = 30^\circ$. Oblicz promień okręgu opisanego.`,
        [r`$2R = \frac{6}{\sin 30^\circ} = 12$.`, r`$R = 6$.`],
        r`$R = 6$`,
      ),
    ],
    pitfalls: ['Zgubiony znak cosinusa kąta rozwartego.', r`Z twierdzenia sinusów wzięte $2R$ zamiast $R$.`, 'Kąt niepołożony naprzeciw boku, z którym go łączysz.'],
  },
  {
    skillId: 'trig-radians',
    minutes: 14,
    intro:
      r`Na rozszerzeniu kąty mierzy się często w radianach: pełny kąt to $2\pi$, półpełny — $\pi$. Funkcje trygonometryczne dostają wtedy dowolny argument i stają się funkcjami okresowymi, których wykresy to fale.`,
    blocks: [
      f(r`180^\circ = \pi \qquad 1^\circ = \frac{\pi}{180}`),
      p(r`Okres podstawowy $\sin x$ i $\cos x$ to $2\pi$, a $\mathrm{tg}\,x$ — $\pi$. Dla $\sin(kx)$ okres to $\frac{2\pi}{|k|}$.`),
      {
        kind: 'figure',
        figure: {
          kind: 'plot',
          alt: 'Wykres funkcji 3 sin x + 2 na przedziale od 0 do 2π: fala z maksimum 5 w x = π/2 i minimum −1 w x = 3π/2.',
          x: [0, 6.6],
          y: [-2, 6],
          curves: [{ fn: wave, label: 'y = 3 sin x + 2' }],
          points: [{ at: [PI / 2, 5] }, { at: [(3 * PI) / 2, -1] }],
          guides: [{ y: 5 }, { y: -1 }],
        },
        caption: 'a·sin x + b: amplituda |a|, środek fali na wysokości b — wartości od b − |a| do b + |a|.',
      },
      tip(r`Dowolny kąt sprowadzasz do $\langle 0, 2\pi)$, odejmując pełne obroty: $\frac{31\pi}{6} = 4\pi + \frac{7\pi}{6}$, więc $\sin\frac{31\pi}{6} = \sin\frac{7\pi}{6}$.`),
      warn(r`$\cos(-x) = \cos x$ (funkcja parzysta), ale $\sin(-x) = -\sin x$ (nieparzysta).`),
    ],
    examples: [
      example(
        r`Zamień $135^\circ$ na radiany.`,
        [r`$135 \cdot \frac{\pi}{180}$.`, r`$= \frac{3\pi}{4}$.`],
        r`$\frac{3\pi}{4}$`,
      ),
      example(
        r`Oblicz $\sin\frac{7\pi}{6}$.`,
        [r`$\frac{7\pi}{6} = \pi + \frac{\pi}{6}$ — III ćwiartka, sinus ujemny.`, r`$\sin\frac{7\pi}{6} = -\sin\frac{\pi}{6} = -\frac12$.`],
        r`$-\frac12$`,
      ),
    ],
    pitfalls: ['Zły znak funkcji w danej ćwiartce.', r`Okres $\sin(3x)$ policzony jako $6\pi$ zamiast $\frac{2\pi}{3}$.`, 'Mieszanie stopni z radianami w jednym rachunku.'],
  },
  {
    skillId: 'trig-formulas',
    minutes: 12,
    intro:
      'Wzory na funkcje sumy i podwojonego kąta pozwalają liczyć wartości dla kątów spoza tabelki i upraszczać wyrażenia. Wszystkie są w tablicach CKE — trzeba umieć je rozpoznać i użyć.',
    blocks: [
      f(r`\sin 2\alpha = 2\sin\alpha\cos\alpha`),
      f(r`\cos 2\alpha = \cos^2\alpha - \sin^2\alpha \qquad = 2\cos^2\alpha - 1 = 1 - 2\sin^2\alpha`),
      f(r`\sin(\alpha \pm \beta) = \sin\alpha\cos\beta \pm \cos\alpha\sin\beta`),
      f(r`\mathrm{tg}\,2\alpha = \frac{2\,\mathrm{tg}\,\alpha}{1 - \mathrm{tg}^2\alpha}`),
      tip(r`Widzisz $\sin x \cos x$? To połowa $\sin 2x$. Widzisz $\cos^2 x - \sin^2 x$? To $\cos 2x$.`),
      warn(r`$\sin 2\alpha \ne 2\sin\alpha$. Sinus nie jest liniowy.`),
    ],
    examples: [
      example(
        r`Oblicz $\sin 15^\circ \cos 15^\circ$.`,
        [r`$= \frac12 \cdot 2\sin 15^\circ\cos 15^\circ = \frac12 \sin 30^\circ$.`, r`$= \frac12 \cdot \frac12 = \frac14$.`],
        r`$\frac14$`,
      ),
      example(
        r`$\cos\alpha = \frac13$. Oblicz $\cos 2\alpha$.`,
        [r`$\cos 2\alpha = 2\cos^2\alpha - 1$.`, r`$= \frac29 - 1 = -\frac79$.`],
        r`$-\frac79$`,
      ),
    ],
    pitfalls: [r`$\sin 2\alpha$ policzone jako $2\sin\alpha$.`, r`Wybrana nie ta postać $\cos 2\alpha$ — dłuższy rachunek.`, 'Zgubiony znak we wzorze na różnicę kątów.'],
  },
  {
    skillId: 'trig-equations',
    minutes: 15,
    intro:
      'Równanie trygonometryczne zwykle ma wiele rozwiązań — funkcje są okresowe. Najpierw znajdujesz rozwiązania w jednym okresie, potem dopisujesz okres albo wybierasz te z podanego przedziału.',
    blocks: [
      {
        kind: 'figure',
        figure: {
          kind: 'plot',
          alt: 'Wykres sinusa na przedziale od 0 do 2π i pozioma prosta y = 1/2. Przecinają się w dwóch punktach: x = π/6 i x = 5π/6.',
          x: [0, 6.6],
          y: [-1.5, 1.5],
          curves: [{ fn: Math.sin, label: 'y = sin x' }],
          guides: [{ y: 0.5 }],
          points: [{ at: [PI / 6, 0.5] }, { at: [(5 * PI) / 6, 0.5] }],
        },
        caption: 'sin x = ½ ma w ⟨0, 2π) dwa rozwiązania: π/6 ≈ 0,52 i 5π/6 ≈ 2,62.',
      },
      f(r`\sin x = \sin a \qquad \iff x = a + 2k\pi \qquad \vee\ x = \pi - a + 2k\pi`),
      f(r`\cos x = \cos a \iff x = \pm a + 2k\pi \qquad \mathrm{tg}\,x = \mathrm{tg}\,a \iff x = a + k\pi`),
      tip(r`Równanie z $\sin^2 x$ i $\sin x$ (albo $\cos 2x$ i $\sin x$) to ukryte równanie kwadratowe: podstaw $t = \sin x$, pamiętając, że $t \in \langle -1, 1 \rangle$.`),
      warn(r`Nie dziel równania przez $\cos x$ ani $\sin x$ — gubisz rozwiązania, w których się zerują. Wyłącz je przed nawias.`),
    ],
    examples: [
      example(
        r`Rozwiąż $2\cos x = 1$ w $\langle 0^\circ, 360^\circ)$.`,
        [r`$\cos x = \frac12$.`, r`$x = 60^\circ$ lub $x = 360^\circ - 60^\circ = 300^\circ$.`],
        r`$x \in \{60^\circ, 300^\circ\}$`,
      ),
      example(
        r`Rozwiąż $\sin 2x = \cos x$ w $\langle 0^\circ, 180^\circ \rangle$.`,
        [r`$2\sin x\cos x - \cos x = 0 \Rightarrow \cos x\,(2\sin x - 1) = 0$.`, r`$\cos x = 0$: $x = 90^\circ$; $\sin x = \frac12$: $x = 30^\circ$ lub $150^\circ$.`],
        r`$x \in \{30^\circ, 90^\circ, 150^\circ\}$`,
      ),
    ],
    pitfalls: ['Tylko jedno rozwiązanie z okresu, choć są dwa.', r`Dzielenie przez $\cos x$ i zgubione rozwiązania.`, r`$t = \sin x$ spoza $\langle -1, 1 \rangle$ zostawione w odpowiedzi.`],
  },
];

// ===========================================================================
// Zadania
// ===========================================================================

const NEW_QUESTIONS: Question[] = [
  // trig-values (dopisane) ----------------------------------------------------
  numeric({
    id: 't-val-1',
    skill: 'trig-values',
    kind: 'foundation',
    difficulty: 1,
    prompt: r`Podaj wartość $\mathrm{tg}\,45^\circ$.`,
    answer: 1,
    verify: () => Math.tan(rad(45)),
    tolerance: 1e-9,
    hints: ['Jak wygląda trójkąt prostokątny z kątem 45°?', 'Jest równoramienny — przyprostokątne są równe.', 'Tangens to iloraz przyprostokątnych.', 'Dwie równe liczby dzielone przez siebie.'],
    steps: [r`Przyprostokątne równe: $\mathrm{tg}\,45^\circ = \frac{a}{a}$.`, r`$= 1$.`],
    errors: [['0.5', r`Pomylone z $\sin 30^\circ$.`, r`$\mathrm{tg}\,45^\circ$: przyprostokątne są równe.`]],
  }),
  numeric({
    id: 't-val-2',
    skill: 'trig-values',
    kind: 'foundation',
    difficulty: 2,
    prompt: r`Przyprostokątne trójkąta prostokątnego mają długości $6$ i $8$. Oblicz cosinus kąta leżącego naprzeciw boku długości $6$.`,
    answer: 0.8,
    variants: ['4/5'],
    verify: () => 8 / Math.hypot(6, 8),
    hints: ['Którego boku brakuje?', r`Przeciwprostokątnej: $\sqrt{36 + 64}$.`, 'Cosinus: bok przy kącie przez przeciwprostokątną.', r`Przy tym kącie leży bok $8$.`],
    steps: [r`Przeciwprostokątna $10$.`, r`$\cos\alpha = \frac{8}{10} = 0{,}8$.`],
    errors: [['0.6', 'Policzony sinus zamiast cosinusa.', 'Cosinus: bok PRZY kącie przez przeciwprostokątną.']],
  }),
  numeric({
    id: 't-val-3',
    skill: 'trig-values',
    kind: 'typical',
    difficulty: 4,
    prompt: r`Kąt $\alpha$ jest ostry i $\mathrm{tg}\,\alpha = 2$. Oblicz $\sin\alpha \cdot \cos\alpha$.`,
    answer: 0.4,
    variants: ['2/5'],
    verify: () => Math.sin(Math.atan(2)) * Math.cos(Math.atan(2)),
    tolerance: 1e-9,
    hints: ['Jaki trójkąt prostokątny ma taki tangens?', r`Przyprostokątne $2$ i $1$ — przeciwprostokątna $\sqrt5$.`, r`$\sin\alpha = \frac{2}{\sqrt5}$, $\cos\alpha = \frac{1}{\sqrt5}$.`, 'Pomnóż.'],
    steps: [r`$\sin\alpha\cos\alpha = \frac{2}{\sqrt5} \cdot \frac{1}{\sqrt5}$.`, r`$= \frac25$.`],
    errors: [['2', 'Pomylony iloczyn sinusa i cosinusa z tangensem.', r`$\mathrm{tg}\,\alpha$ to iloraz, a nie iloczyn.`]],
  }),
  numeric({
    id: 't-val-4',
    skill: 'trig-values',
    kind: 'transfer',
    difficulty: 5,
    prompt: r`Z punktu $A$ szczyt wieży widać pod kątem $30^\circ$, a z punktu $B$, położonego $20$ m bliżej wieży, pod kątem $60^\circ$. Oblicz wysokość wieży w metrach (z dokładnością do $0{,}01$).`,
    answer: 17.32,
    tolerance: 0.01,
    verify: () => 20 / (1 / Math.tan(rad(30)) - 1 / Math.tan(rad(60))),
    figure: {
      kind: 'geometry',
      alt: 'Punkty A i B na ziemi w odległości 20, dalej podstawa wieży W i szczyt T. Kąt przy A ma 30°, przy B 60°, przy W kąt prosty.',
      points: { A: [0, 0], B: [20, 0], W: [30, 0], T: [30, 17.32] },
      segments: [
        { from: 'A', to: 'B', label: '20' },
        { from: 'B', to: 'W' },
        { from: 'W', to: 'T', label: 'h' },
        { from: 'A', to: 'T', dashed: true },
        { from: 'B', to: 'T', dashed: true },
      ],
      angles: [
        { at: 'A', from: 'W', to: 'T', label: '30°' },
        { at: 'B', from: 'W', to: 'T', label: '60°' },
        { at: 'W', from: 'B', to: 'T', right: true },
      ],
    },
    hints: ['Jak wyrazić odległości AW i BW przez wysokość h?', r`$AW = \frac{h}{\mathrm{tg}\,30^\circ} = h\sqrt3$, $BW = \frac{h}{\mathrm{tg}\,60^\circ} = \frac{h}{\sqrt3}$.`, r`$AW - BW = 20$.`, r`$h\left(\sqrt3 - \frac{1}{\sqrt3}\right) = 20 \Rightarrow h \cdot \frac{2}{\sqrt3} = 20$.`],
    steps: [r`$h = 10\sqrt3$.`, r`$h \approx 17{,}32$ m.`],
    errors: [['10', 'Pominięty czynnik √3.', r`$h = \frac{20\sqrt3}{2}$.`]],
  }),

  // trig-identities (dopisane) ------------------------------------------------
  choice({
    id: 't-id-1',
    skill: 'trig-identities',
    kind: 'foundation',
    difficulty: 1,
    prompt: r`Wyrażenie $\sin^2\alpha + \cos^2\alpha - 1$ jest równe`,
    choices: [r`$0$`, r`$1$`, r`$2\sin^2\alpha$`, r`$-1$`],
    answer: 'A',
    verify: () => Math.sin(0.7) ** 2 + Math.cos(0.7) ** 2 - 1,
    hints: ['Który wzór łączy kwadraty sinusa i cosinusa?', 'Jedynka trygonometryczna.', r`$\sin^2\alpha + \cos^2\alpha = 1$.`, 'Odejmij jedynkę.'],
    steps: [r`$\sin^2\alpha + \cos^2\alpha = 1$.`, r`$1 - 1 = 0$.`],
    errors: [
      ['B', 'Pominięte odjęcie jedynki.', 'Wyrażenie kończy się „− 1”.'],
      ['C', r`Jedynka zastąpiona przez $\cos^2\alpha$.`, r`$1 = \sin^2\alpha + \cos^2\alpha$.`],
      ['D', 'Suma kwadratów uznana za zero.', r`$\sin^2\alpha + \cos^2\alpha = 1$.`],
    ],
  }),
  numeric({
    id: 't-id-2',
    skill: 'trig-identities',
    kind: 'foundation',
    difficulty: 2,
    prompt: r`Kąt $\alpha$ jest ostry i $\cos\alpha = 0{,}8$. Oblicz $\sin\alpha$.`,
    answer: 0.6,
    variants: ['3/5'],
    verify: () => Math.sqrt(1 - 0.64),
    tolerance: 1e-9,
    hints: ['Który wzór łączy sinus z cosinusem?', r`$\sin^2\alpha = 1 - \cos^2\alpha$.`, r`$\cos^2\alpha = 0{,}64$.`, 'Weź dodatni pierwiastek — kąt ostry.'],
    steps: [r`$\sin^2\alpha = 0{,}36$.`, r`$\sin\alpha = 0{,}6$.`],
    errors: [['0.2', r`Policzone $1 - \cos\alpha$.`, r`Jedynka dotyczy kwadratów: $\sin^2\alpha = 1 - \cos^2\alpha$.`]],
  }),
  numeric({
    id: 't-id-3',
    skill: 'trig-identities',
    kind: 'typical',
    difficulty: 4,
    prompt: r`Kąt $\alpha$ jest ostry i $\mathrm{tg}\,\alpha = 3$. Oblicz $\frac{\sin\alpha + \cos\alpha}{\sin\alpha - \cos\alpha}$.`,
    answer: 2,
    verify: () => {
      const a = Math.atan(3);
      return (Math.sin(a) + Math.cos(a)) / (Math.sin(a) - Math.cos(a));
    },
    tolerance: 1e-9,
    hints: ['Jak pozbyć się sinusa i cosinusa naraz?', r`Podziel licznik i mianownik przez $\cos\alpha$.`, r`$\frac{\mathrm{tg}\,\alpha + 1}{\mathrm{tg}\,\alpha - 1}$.`, r`$\frac{3 + 1}{3 - 1}$.`],
    steps: [r`$\frac{\mathrm{tg}\,\alpha + 1}{\mathrm{tg}\,\alpha - 1} = \frac{4}{2}$.`, r`$= 2$.`],
    errors: [['0.5', 'Odwrócony ułamek.', 'Licznik to suma, mianownik — różnica.']],
  }),
  numeric({
    id: 't-id-4',
    skill: 'trig-identities',
    kind: 'transfer',
    difficulty: 5,
    prompt: r`Kąt $\alpha$ jest ostry i $\mathrm{tg}\,\alpha + \frac{1}{\mathrm{tg}\,\alpha} = 4$. Oblicz $\sin\alpha\cos\alpha$.`,
    answer: 0.25,
    variants: ['1/4'],
    verify: () => {
      // t + 1/t = 4 -> t = 2 +- sqrt 3; bierzemy dowolny
      const a = Math.atan(2 + Math.sqrt(3));
      return Math.sin(a) * Math.cos(a);
    },
    tolerance: 1e-9,
    hints: ['Jak zapisać tangens przez sinus i cosinus?', r`$\frac{\sin\alpha}{\cos\alpha} + \frac{\cos\alpha}{\sin\alpha}$.`, r`Wspólny mianownik: $\frac{\sin^2\alpha + \cos^2\alpha}{\sin\alpha\cos\alpha}$.`, r`Licznik to jedynka: $\frac{1}{\sin\alpha\cos\alpha} = 4$.`],
    steps: [r`$\frac{1}{\sin\alpha\cos\alpha} = 4$.`, r`$\sin\alpha\cos\alpha = \frac14$.`],
    errors: [['4', 'Podana odwrotność szukanej wartości.', r`$\frac{1}{\sin\alpha\cos\alpha} = 4$ — odwróć.`]],
  }),

  // trig-obtuse ---------------------------------------------------------------
  numeric({
    id: 't-obt-1',
    skill: 'trig-obtuse',
    kind: 'foundation',
    difficulty: 1,
    prompt: r`Podaj wartość $\sin 150^\circ$.`,
    answer: 0.5,
    variants: ['1/2'],
    verify: () => Math.sin(rad(150)),
    tolerance: 1e-9,
    hints: [r`Jak sprowadzić kąt $150^\circ$ do ostrego?`, r`$150^\circ = 180^\circ - 30^\circ$.`, r`$\sin(180^\circ - \alpha) = \sin\alpha$.`, r`$\sin 30^\circ$.`],
    steps: [r`$\sin 150^\circ = \sin 30^\circ$.`, r`$= \frac12$.`],
    errors: [['-0.5', 'Ujemny sinus kąta rozwartego.', 'Sinus kąta z (0°, 180°) jest dodatni.']],
  }),
  numeric({
    id: 't-obt-2',
    skill: 'trig-obtuse',
    kind: 'foundation',
    difficulty: 2,
    prompt: r`Podaj wartość $\cos 120^\circ$.`,
    answer: -0.5,
    variants: ['-1/2'],
    verify: () => Math.cos(rad(120)),
    tolerance: 1e-9,
    hints: [r`Jak sprowadzić kąt $120^\circ$ do ostrego?`, r`$120^\circ = 180^\circ - 60^\circ$.`, r`$\cos(180^\circ - \alpha) = -\cos\alpha$.`, r`$-\cos 60^\circ$.`],
    steps: [r`$\cos 120^\circ = -\cos 60^\circ$.`, r`$= -\frac12$.`],
    errors: [['0.5', 'Dodatni cosinus kąta rozwartego.', 'Cosinus kąta rozwartego jest ujemny.']],
  }),
  choice({
    id: 't-obt-3',
    skill: 'trig-obtuse',
    kind: 'typical',
    difficulty: 2,
    prompt: 'Która z liczb jest ujemna?',
    choices: [r`$\cos 100^\circ$`, r`$\sin 100^\circ$`, r`$\sin 30^\circ$`, r`$\cos 10^\circ$`],
    answer: 'A',
    hints: ['Jaki znak mają sinus i cosinus kąta rozwartego?', 'Sinus dodatni, cosinus ujemny.', r`$100^\circ$ to kąt rozwarty.`, 'Kąty ostre mają oba znaki dodatnie.'],
    steps: [r`$\cos 100^\circ = -\cos 80^\circ < 0$.`, 'Pozostałe liczby są dodatnie.'],
    errors: [
      ['B', 'Sinus kąta rozwartego uznany za ujemny.', 'Sinus kąta z (0°, 180°) jest dodatni.'],
      ['C', r`$\sin 30^\circ = \frac12 > 0$.`, 'Funkcje kąta ostrego są dodatnie.'],
      ['D', r`$\cos 10^\circ > 0$.`, 'Funkcje kąta ostrego są dodatnie.'],
    ],
  }),
  numeric({
    id: 't-obt-4',
    skill: 'trig-obtuse',
    kind: 'typical',
    difficulty: 3,
    prompt: r`Podaj wartość $\mathrm{tg}\,135^\circ$.`,
    answer: -1,
    verify: () => Math.tan(rad(135)),
    tolerance: 1e-9,
    hints: [r`Jak sprowadzić $135^\circ$ do kąta ostrego?`, r`$135^\circ = 180^\circ - 45^\circ$.`, r`$\mathrm{tg}\,(180^\circ - \alpha) = -\mathrm{tg}\,\alpha$.`, r`$-\mathrm{tg}\,45^\circ$.`],
    steps: [r`$\mathrm{tg}\,135^\circ = -\mathrm{tg}\,45^\circ$.`, r`$= -1$.`],
    errors: [['1', 'Dodatni tangens kąta rozwartego.', 'Tangens kąta rozwartego jest ujemny.']],
  }),
  numeric({
    id: 't-obt-5',
    skill: 'trig-obtuse',
    kind: 'typical',
    difficulty: 3,
    prompt: r`Pod jakim kątem (w stopniach) prosta $y = \sqrt3\,x + 2$ jest nachylona do osi $x$?`,
    answer: 60,
    verify: () => (Math.atan(Math.sqrt(3)) * 180) / PI,
    tolerance: 1e-9,
    hints: ['Co mówi współczynnik kierunkowy o kącie nachylenia?', r`$\mathrm{tg}\,\alpha = a$.`, r`$\mathrm{tg}\,\alpha = \sqrt3$.`, r`Dla którego kąta z tabelki tangens to $\sqrt3$?`],
    steps: [r`$\mathrm{tg}\,\alpha = \sqrt3$.`, r`$\alpha = 60^\circ$.`],
    errors: [['30', r`Pomylone $\mathrm{tg}\,30^\circ$ z $\mathrm{tg}\,60^\circ$.`, r`$\mathrm{tg}\,30^\circ = \frac{\sqrt3}{3}$, $\mathrm{tg}\,60^\circ = \sqrt3$.`]],
  }),
  numeric({
    id: 't-obt-6',
    skill: 'trig-obtuse',
    kind: 'typical',
    difficulty: 3,
    prompt: r`Kąt $\alpha$ jest rozwarty i $\sin\alpha = \frac35$. Oblicz $\cos\alpha$.`,
    answer: -0.8,
    variants: ['-4/5'],
    verify: () => Math.cos(PI - Math.asin(0.6)),
    tolerance: 1e-9,
    hints: ['Z którego wzoru policzysz cosinus?', r`$\cos^2\alpha = 1 - \frac{9}{25}$.`, 'Jaki znak ma cosinus kąta rozwartego?', 'Ujemny.'],
    steps: [r`$\cos^2\alpha = \frac{16}{25}$.`, r`Kąt rozwarty: $\cos\alpha = -\frac45$.`],
    errors: [['0.8', 'Pominięty znak cosinusa kąta rozwartego.', 'Dla kąta rozwartego cosinus jest ujemny.']],
  }),
  numeric({
    id: 't-obt-7',
    skill: 'trig-obtuse',
    kind: 'typical',
    difficulty: 4,
    prompt: r`Kąt $\alpha$ jest rozwarty i $\mathrm{tg}\,\alpha = -2$. Oblicz $\cos^2\alpha$.`,
    answer: 0.2,
    variants: ['1/5'],
    verify: () => Math.cos(PI - Math.atan(2)) ** 2,
    tolerance: 1e-9,
    hints: ['Jak połączyć tangens z cosinusem?', r`$\sin\alpha = \mathrm{tg}\,\alpha \cdot \cos\alpha = -2\cos\alpha$.`, r`Wstaw do jedynki: $4\cos^2\alpha + \cos^2\alpha = 1$.`, 'Wyznacz cos²α.'],
    steps: [r`$5\cos^2\alpha = 1$.`, r`$\cos^2\alpha = \frac15$.`],
    errors: [['0.8', r`Policzone $\sin^2\alpha$ zamiast $\cos^2\alpha$.`, r`$\cos^2\alpha = \frac{1}{1 + \mathrm{tg}^2\alpha}$.`]],
  }),
  numeric({
    id: 't-obt-8',
    skill: 'trig-obtuse',
    kind: 'transfer',
    difficulty: 4,
    prompt: r`Prosta przechodzi przez punkty $A = (1, 2)$ i $B = (4, 2 - 3\sqrt3)$. Pod jakim kątem (w stopniach) jest nachylona do osi $x$?`,
    answer: 120,
    verify: () => 180 + (Math.atan(-Math.sqrt(3)) * 180) / PI,
    tolerance: 1e-9,
    hints: ['Jak obliczyć współczynnik kierunkowy z dwóch punktów?', r`$a = \frac{y_B - y_A}{x_B - x_A}$.`, r`$a = \frac{-3\sqrt3}{3} = -\sqrt3$.`, r`Tangens ujemny — kąt rozwarty: $180^\circ - 60^\circ$.`],
    steps: [r`$\mathrm{tg}\,\alpha = -\sqrt3$.`, r`$\alpha = 120^\circ$.`],
    errors: [['60', 'Pominięty znak — wzięty kąt ostry.', 'Ujemny współczynnik kierunkowy oznacza kąt rozwarty.']],
  }),

  // trig-area -----------------------------------------------------------------
  numeric({
    id: 't-area-1',
    skill: 'trig-area',
    kind: 'foundation',
    difficulty: 1,
    prompt: r`Boki trójkąta mają długości $6$ i $8$, a kąt między nimi ma $30^\circ$. Oblicz pole trójkąta.`,
    answer: 12,
    verify: () => 0.5 * 6 * 8 * Math.sin(rad(30)),
    tolerance: 1e-9,
    hints: ['Jaki wzór na pole wykorzystuje dwa boki i kąt?', r`$P = \frac12 ab\sin\gamma$.`, r`$\sin 30^\circ = \frac12$.`, r`$\frac12 \cdot 6 \cdot 8 \cdot \frac12$.`],
    steps: [r`$P = 24 \cdot \frac12$.`, r`$= 12$.`],
    errors: [['24', 'Pominięty sinus kąta.', r`Pole: $\frac12 ab \sin\gamma$.`]],
  }),
  numeric({
    id: 't-area-2',
    skill: 'trig-area',
    kind: 'foundation',
    difficulty: 2,
    prompt: r`Równoległobok ma boki $5$ i $4$ oraz kąt ostry $30^\circ$. Oblicz jego pole.`,
    answer: 10,
    verify: () => 5 * 4 * Math.sin(rad(30)),
    tolerance: 1e-9,
    hints: ['Jaki wzór na pole równoległoboku zawiera sinus?', r`$P = ab\sin\alpha$.`, r`$5 \cdot 4 \cdot \sin 30^\circ$.`, r`$\sin 30^\circ = \frac12$.`],
    steps: [r`$P = 20 \cdot \frac12$.`, r`$= 10$.`],
    errors: [['5', r`Dodane $\frac12$ jak w trójkącie.`, 'Równoległobok to dwa trójkąty — bez ½.']],
  }),
  numeric({
    id: 't-area-3',
    skill: 'trig-area',
    kind: 'typical',
    difficulty: 3,
    prompt: r`Romb ma bok długości $6$ i kąt rozwarty $150^\circ$. Oblicz jego pole.`,
    answer: 18,
    verify: () => 36 * Math.sin(rad(150)),
    tolerance: 1e-9,
    hints: ['Jakim wzorem policzysz pole rombu z boku i kąta?', r`$P = a^2\sin\alpha$.`, r`$\sin 150^\circ = \sin 30^\circ$.`, r`$36 \cdot \frac12$.`],
    steps: [r`$P = 36 \cdot \sin 150^\circ$.`, r`$= 18$.`],
    errors: [['36', 'Pominięty sinus kąta.', r`Pole rombu: $a^2\sin\alpha$.`]],
  }),
  numeric({
    id: 't-area-4',
    skill: 'trig-area',
    kind: 'typical',
    difficulty: 3,
    prompt: r`Trójkąt równoramienny ma ramiona długości $10$ i kąt między nimi $30^\circ$. Oblicz jego pole.`,
    answer: 25,
    verify: () => 0.5 * 10 * 10 * Math.sin(rad(30)),
    tolerance: 1e-9,
    hints: ['Które boki i który kąt wstawić do wzoru?', 'Dwa ramiona i kąt między nimi.', r`$\frac12 \cdot 10 \cdot 10 \cdot \sin 30^\circ$.`, 'Policz.'],
    steps: [r`$P = 50 \cdot \frac12$.`, r`$= 25$.`],
    errors: [['50', r`Pominięte $\sin 30^\circ$.`, r`Pole: $\frac12 ab\sin\gamma$.`]],
  }),
  numeric({
    id: 't-area-5',
    skill: 'trig-area',
    kind: 'typical',
    difficulty: 3,
    prompt: r`W trójkącie prostokątnym przeciwprostokątna ma długość $10$, a sinus jednego z kątów ostrych wynosi $0{,}6$. Oblicz pole trójkąta.`,
    answer: 24,
    verify: () => (0.5 * (10 * 0.6) * (10 * 0.8)),
    tolerance: 1e-9,
    hints: ['Jak obliczyć przyprostokątne z sinusa?', r`Naprzeciw kąta: $10 \cdot 0{,}6$.`, r`Druga przyprostokątna z Pitagorasa albo z cosinusa.`, r`Przyprostokątne $6$ i $8$ — pole to połowa ich iloczynu.`],
    steps: [r`Przyprostokątne: $6$ i $8$.`, r`$P = \frac12 \cdot 6 \cdot 8 = 24$.`],
    errors: [['48', 'Pominięte ½.', 'Pole trójkąta prostokątnego to połowa iloczynu przyprostokątnych.']],
  }),
  numeric({
    id: 't-area-6',
    skill: 'trig-area',
    kind: 'typical',
    difficulty: 4,
    prompt: r`Pole trójkąta o bokach $5$ i $12$ wynosi $15$, a kąt między tymi bokami jest rozwarty. Podaj ten kąt w stopniach.`,
    answer: 150,
    verify: () => 180 - (Math.asin((2 * 15) / (5 * 12)) * 180) / PI,
    tolerance: 1e-9,
    hints: ['Jakie równanie daje wzór na pole?', r`$15 = \frac12 \cdot 5 \cdot 12 \cdot \sin\gamma$.`, r`$\sin\gamma = \frac12$ — są dwa takie kąty w $(0^\circ, 180^\circ)$.`, 'Wybierz rozwarty.'],
    steps: [r`$\sin\gamma = \frac12$: $\gamma = 30^\circ$ lub $150^\circ$.`, r`Rozwarty: $\gamma = 150^\circ$.`],
    errors: [['30', 'Wybrany kąt ostry.', 'Treść mówi o kącie rozwartym.']],
  }),
  numeric({
    id: 't-area-7',
    skill: 'trig-area',
    kind: 'transfer',
    difficulty: 4,
    prompt: r`Działka ma kształt trójkąta. Dwa jej boki mają $40$ m i $50$ m, a kąt między nimi ma $150^\circ$. Oblicz powierzchnię działki w $\mathrm{m}^2$.`,
    answer: 500,
    verify: () => 0.5 * 40 * 50 * Math.sin(rad(150)),
    tolerance: 1e-9,
    hints: ['Jaki wzór na pole pasuje do tych danych?', r`$P = \frac12 ab\sin\gamma$.`, r`$\sin 150^\circ = \sin 30^\circ$.`, r`$\frac12 \cdot 40 \cdot 50 \cdot \frac12$.`],
    steps: [r`$P = 1000 \cdot \frac12$.`, r`$= 500\ \mathrm{m}^2$.`],
    errors: [['1000', 'Pominięty sinus kąta.', r`$\sin 150^\circ = \frac12$.`]],
  }),
  numeric({
    id: 't-area-8',
    skill: 'trig-area',
    kind: 'transfer',
    difficulty: 5,
    prompt: r`Przekątne czworokąta wypukłego mają długości $8$ i $10$ i przecinają się pod kątem $30^\circ$. Oblicz pole czworokąta.`,
    answer: 20,
    verify: () => {
      // Dowolny podział przekątnych: p1 + p2 = 8, q1 + q2 = 10; suma pól czterech trójkątów.
      const [p1, p2, q1, q2] = [3, 5, 4, 6];
      const s = Math.sin(rad(30));
      return 0.5 * s * (p1 * q1 + q1 * p2 + p2 * q2 + q2 * p1);
    },
    tolerance: 1e-9,
    hints: ['Na jakie części przekątne dzielą czworokąt?', 'Na cztery trójkąty ze wspólnym wierzchołkiem w punkcie przecięcia.', r`Kąty przy tym punkcie to $30^\circ$ i $150^\circ$ — o równych sinusach.`, r`Suma pól: $\frac12 \sin 30^\circ \cdot (p_1 + p_2)(q_1 + q_2)$.`],
    steps: [r`$P = \frac12 \cdot d_1 d_2 \sin\varphi$.`, r`$= \frac12 \cdot 8 \cdot 10 \cdot \frac12 = 20$.`],
    errors: [['40', 'Pominięty sinus kąta.', r`$P = \frac12 d_1 d_2 \sin\varphi$.`]],
  }),

  // trig-laws -----------------------------------------------------------------
  numeric({
    id: 't-law-1',
    skill: 'trig-laws',
    kind: 'foundation',
    difficulty: 1,
    prompt: r`W trójkącie $a = 3$, $b = 5$, a kąt między nimi $\gamma = 60^\circ$. Oblicz $c^2$.`,
    answer: 19,
    verify: () => 9 + 25 - 2 * 3 * 5 * Math.cos(rad(60)),
    tolerance: 1e-9,
    hints: ['Które twierdzenie łączy trzy boki i kąt?', 'Twierdzenie cosinusów.', r`$c^2 = 9 + 25 - 2 \cdot 3 \cdot 5 \cdot \cos 60^\circ$.`, r`$\cos 60^\circ = \frac12$.`],
    steps: [r`$c^2 = 34 - 15$.`, r`$= 19$.`],
    errors: [['34', 'Pominięty składnik z cosinusem.', 'Twierdzenie cosinusów ma trzeci składnik.']],
  }),
  numeric({
    id: 't-law-2',
    skill: 'trig-laws',
    kind: 'foundation',
    difficulty: 2,
    prompt: r`W trójkącie bok $a = 6$ leży naprzeciw kąta $\alpha = 30^\circ$. Oblicz promień okręgu opisanego na tym trójkącie.`,
    answer: 6,
    verify: () => 6 / (2 * Math.sin(rad(30))),
    tolerance: 1e-9,
    hints: ['Które twierdzenie łączy bok, kąt naprzeciw niego i promień?', 'Twierdzenie sinusów.', r`$\frac{a}{\sin\alpha} = 2R$.`, r`$\frac{6}{\frac12} = 2R$.`],
    steps: [r`$2R = 12$.`, r`$R = 6$.`],
    errors: [['12', r`Podane $2R$ zamiast $R$.`, r`Twierdzenie sinusów daje $2R$.`]],
  }),
  choice({
    id: 't-law-3',
    skill: 'trig-laws',
    kind: 'typical',
    difficulty: 2,
    prompt: r`Trójkąt o bokach $4$, $5$, $7$ jest`,
    choices: ['rozwartokątny', 'prostokątny', 'ostrokątny', 'taki trójkąt nie istnieje'],
    answer: 'A',
    hints: ['Jak rozpoznać rodzaj kąta naprzeciw najdłuższego boku?', r`Porównaj $7^2$ z $4^2 + 5^2$.`, r`$49$ i $41$.`, r`$c^2 > a^2 + b^2$ oznacza kąt rozwarty.`],
    steps: [r`$49 > 41$.`, 'Kąt naprzeciw boku 7 jest rozwarty.'],
    errors: [
      ['B', 'Nie sprawdzono równości z Pitagorasa.', r`$49 \ne 41$.`],
      ['C', 'Odwrócona nierówność.', r`$c^2 > a^2 + b^2$ — kąt rozwarty.`],
      ['D', 'Nierówność trójkąta jest spełniona.', r`$4 + 5 > 7$ — trójkąt istnieje.`],
    ],
  }),
  numeric({
    id: 't-law-4',
    skill: 'trig-laws',
    kind: 'typical',
    difficulty: 3,
    prompt: r`Trójkąt ma boki $5$, $7$, $8$. Oblicz miarę kąta leżącego naprzeciw boku $7$ (w stopniach).`,
    answer: 60,
    verify: () => (Math.acos((25 + 64 - 49) / (2 * 5 * 8)) * 180) / PI,
    tolerance: 1e-9,
    hints: ['Które twierdzenie da cosinus kąta z trzech boków?', r`$49 = 25 + 64 - 2 \cdot 5 \cdot 8 \cos\gamma$.`, r`$80\cos\gamma = 40$.`, 'Dla jakiego kąta cosinus to ½?'],
    steps: [r`$\cos\gamma = \frac12$.`, r`$\gamma = 60^\circ$.`],
    errors: [['30', 'Pomylony kąt o sinusie ½ z kątem o cosinusie ½.', r`$\cos 60^\circ = \frac12$.`]],
  }),
  numeric({
    id: 't-law-5',
    skill: 'trig-laws',
    kind: 'typical',
    difficulty: 3,
    prompt: r`W trójkącie $a = 5$, $b = 6$ oraz $\sin\alpha = \frac13$ ($\alpha$ leży naprzeciw $a$). Oblicz $\sin\beta$.`,
    answer: 0.4,
    variants: ['2/5'],
    verify: () => (6 * (1 / 3)) / 5,
    tolerance: 1e-9,
    hints: ['Które twierdzenie łączy boki z sinusami kątów naprzeciw?', 'Twierdzenie sinusów.', r`$\frac{5}{\frac13} = \frac{6}{\sin\beta}$.`, r`$\sin\beta = \frac{6 \cdot \frac13}{5}$.`],
    steps: [r`$\sin\beta = \frac{2}{5}$.`, r`$= 0{,}4$.`],
    errors: [['2.5', 'Odwrócona proporcja.', r`$\sin\beta = \frac{b\sin\alpha}{a}$.`]],
  }),
  numeric({
    id: 't-law-6',
    skill: 'trig-laws',
    kind: 'typical',
    difficulty: 4,
    prompt: r`Trójkąt ma boki $7$, $8$, $13$. Oblicz miarę jego największego kąta (w stopniach).`,
    answer: 120,
    verify: () => (Math.acos((49 + 64 - 169) / (2 * 7 * 8)) * 180) / PI,
    tolerance: 1e-9,
    hints: ['Naprzeciw którego boku leży największy kąt?', 'Naprzeciw najdłuższego.', r`$169 = 49 + 64 - 112\cos\gamma$.`, r`$\cos\gamma = -\frac12$.`],
    steps: [r`$112\cos\gamma = -56$.`, r`$\gamma = 120^\circ$.`],
    errors: [['60', 'Zgubiony minus cosinusa.', r`$\cos\gamma = -\frac12$ — kąt rozwarty.`]],
  }),
  numeric({
    id: 't-law-7',
    skill: 'trig-laws',
    kind: 'transfer',
    difficulty: 4,
    prompt: r`Dwa statki wypłynęły z portu. Jeden przepłynął $30$ km, drugi $50$ km, a ich kursy tworzą kąt $120^\circ$. Jaka jest odległość między statkami (w km)?`,
    answer: 70,
    verify: () => Math.sqrt(900 + 2500 - 2 * 30 * 50 * Math.cos(rad(120))),
    tolerance: 1e-9,
    hints: ['Jaki trójkąt tworzą port i dwa statki?', 'Znasz dwa boki i kąt między nimi — twierdzenie cosinusów.', r`$d^2 = 900 + 2500 - 3000\cos 120^\circ$.`, r`$\cos 120^\circ = -\frac12$.`],
    steps: [r`$d^2 = 3400 + 1500 = 4900$.`, r`$d = 70$ km.`],
    errors: [['43.59', r`Przyjęte $\cos 120^\circ = \frac12$.`, 'Cosinus kąta rozwartego jest ujemny.']],
  }),
  numeric({
    id: 't-law-8',
    skill: 'trig-laws',
    kind: 'transfer',
    difficulty: 5,
    prompt: r`W trójkącie $ABC$: $|AC| = 3$, $|BC| = 5$, $|\angle ACB| = 120^\circ$. Oblicz $\cos|\angle BAC|$ (z dokładnością do $0{,}001$).`,
    answer: '11/14',
    variants: ['0.786'],
    tolerance: 0.001,
    verify: () => {
      const ab = Math.sqrt(9 + 25 - 2 * 3 * 5 * Math.cos(rad(120)));
      return (ab * ab + 9 - 25) / (2 * ab * 3);
    },
    hints: ['Którego boku brakuje, żeby policzyć kąt przy A?', r`$|AB|$ — z twierdzenia cosinusów dla kąta $C$.`, r`$|AB|^2 = 9 + 25 + 15$.`, r`Teraz twierdzenie cosinusów dla kąta $A$: $25 = |AB|^2 + 9 - 2 \cdot |AB| \cdot 3 \cos A$.`],
    steps: [r`$|AB| = 7$.`, r`$\cos A = \frac{49 + 9 - 25}{42} = \frac{33}{42} = \frac{11}{14}$.`],
    errors: [['0.5', r`Wzięty cosinus kąta $C$ bez znaku.`, 'Pytanie dotyczy kąta przy wierzchołku A.']],
  }),

  // trig-radians --------------------------------------------------------------
  numeric({
    id: 't-rad-1',
    skill: 'trig-radians',
    kind: 'foundation',
    difficulty: 1,
    prompt: r`Zamień $\frac{\pi}{3}$ radianów na stopnie.`,
    answer: 60,
    verify: () => 180 / 3,
    hints: [r`Ilu stopniom odpowiada $\pi$?`, r`$\pi = 180^\circ$.`, r`$\frac{180^\circ}{3}$.`, 'Podziel.'],
    steps: [r`$\frac{\pi}{3} = \frac{180^\circ}{3}$.`, r`$= 60^\circ$.`],
    errors: [['30', r`Pomylone z $\frac{\pi}{6}$.`, r`$\frac{\pi}{3} = \frac{180^\circ}{3}$.`]],
  }),
  numeric({
    id: 't-rad-2',
    skill: 'trig-radians',
    kind: 'foundation',
    difficulty: 2,
    prompt: r`Zamień $135^\circ$ na radiany. Wynik ma postać $k\pi$ — podaj $k$.`,
    answer: '3/4',
    variants: ['0.75'],
    verify: () => 135 / 180,
    hints: [r`Ile radianów ma $1^\circ$?`, r`$\frac{\pi}{180}$.`, r`$135 \cdot \frac{\pi}{180}$.`, 'Skróć ułamek 135/180.'],
    steps: [r`$\frac{135}{180} = \frac34$.`, r`$135^\circ = \frac{3\pi}{4}$.`],
    errors: [['4/3', 'Odwrócony ułamek.', r`Stopnie mnożysz przez $\frac{\pi}{180}$.`]],
  }),
  choice({
    id: 't-rad-3',
    skill: 'trig-radians',
    kind: 'typical',
    difficulty: 2,
    prompt: r`Okres podstawowy funkcji $f(x) = \sin x$ to`,
    choices: [r`$2\pi$`, r`$\pi$`, r`$\frac{\pi}{2}$`, r`$4\pi$`],
    answer: 'A',
    hints: ['Po jakim kącie punkt na okręgu wraca na swoje miejsce?', 'Po pełnym obrocie.', r`Pełny obrót to $360^\circ$.`, r`$360^\circ = 2\pi$.`],
    steps: ['Sinus powtarza wartości co pełny obrót.', r`Okres: $2\pi$.`],
    errors: [
      ['B', r`Pomylony z okresem tangensa.`, r`$\pi$ to okres $\mathrm{tg}\,x$, a nie $\sin x$.`],
      ['C', 'Ćwiartka obrotu.', 'Wartości sinusa powtarzają się co pełny obrót.'],
      ['D', 'To wielokrotność okresu, nie okres podstawowy.', 'Okres podstawowy to najmniejszy okres.'],
    ],
  }),
  numeric({
    id: 't-rad-4',
    skill: 'trig-radians',
    kind: 'typical',
    difficulty: 3,
    prompt: r`Oblicz $\sin\frac{7\pi}{6}$.`,
    answer: -0.5,
    variants: ['-1/2'],
    verify: () => Math.sin((7 * PI) / 6),
    tolerance: 1e-9,
    hints: [r`W której ćwiartce leży kąt $\frac{7\pi}{6}$?`, r`$\frac{7\pi}{6} = \pi + \frac{\pi}{6}$ — w trzeciej.`, 'Jaki znak ma sinus w III ćwiartce?', r`Ujemny: $-\sin\frac{\pi}{6}$.`],
    steps: [r`$\sin\frac{7\pi}{6} = -\sin\frac{\pi}{6}$.`, r`$= -\frac12$.`],
    errors: [['0.5', 'Zgubiony znak w III ćwiartce.', 'W III ćwiartce sinus jest ujemny.']],
  }),
  numeric({
    id: 't-rad-5',
    skill: 'trig-radians',
    kind: 'typical',
    difficulty: 3,
    prompt: r`Podaj największą wartość funkcji $f(x) = 3 - 2\cos x$.`,
    answer: 5,
    verify: () => Math.max(...Array.from({ length: 1000 }, (_, i) => 3 - 2 * Math.cos((i / 1000) * 2 * PI))),
    tolerance: 1e-6,
    hints: [r`W jakim przedziale leżą wartości $\cos x$?`, r`$\cos x \in \langle -1, 1 \rangle$.`, r`$3 - 2\cos x$ jest największe, gdy $\cos x$ jest najmniejszy.`, r`Wstaw $\cos x = -1$.`],
    steps: [r`$\cos x = -1$: $3 + 2$.`, r`Największa wartość: $5$.`],
    errors: [['1', r`Wstawione $\cos x = 1$ — to najmniejsza wartość.`, r`Minus przed $2\cos x$ odwraca: max dla $\cos x = -1$.`]],
  }),
  numeric({
    id: 't-rad-6',
    skill: 'trig-radians',
    kind: 'typical',
    difficulty: 4,
    prompt: r`Okres podstawowy funkcji $f(x) = \sin 3x$ ma postać $k\pi$. Podaj $k$.`,
    answer: '2/3',
    variants: ['0.667', '0.6667'],
    tolerance: 0.001,
    verify: () => 2 / 3,
    hints: ['Jak mnożenie argumentu zmienia okres?', r`Okres $\sin(bx)$ to $\frac{2\pi}{|b|}$.`, r`$b = 3$.`, r`$\frac{2\pi}{3}$.`],
    steps: [r`$T = \frac{2\pi}{3}$.`, r`$k = \frac23$.`],
    errors: [['6', 'Okres pomnożony zamiast podzielony.', 'Szybsze „kręcenie” skraca okres.']],
  }),
  numeric({
    id: 't-rad-7',
    skill: 'trig-radians',
    kind: 'transfer',
    difficulty: 4,
    prompt: r`Na rysunku jest wykres funkcji $f(x) = a\sin x + b$, gdzie $a > 0$. Oblicz $a \cdot b$.`,
    figure: {
      kind: 'plot',
      alt: 'Fala sinusoidalna na przedziale od 0 do 2π z maksimum 5 w x = π/2 i minimum −1 w x = 3π/2.',
      x: [0, 6.6],
      y: [-2, 6],
      curves: [{ fn: wave }],
      points: [{ at: [PI / 2, 5] }, { at: [(3 * PI) / 2, -1] }],
      guides: [{ y: 5 }, { y: -1 }],
    },
    answer: 6,
    verify: () => ((5 - -1) / 2) * ((5 + -1) / 2),
    hints: ['Jakie są największa i najmniejsza wartość na wykresie?', r`$5$ i $-1$.`, r`$b + a = 5$ oraz $b - a = -1$.`, 'Rozwiąż układ.'],
    steps: [r`$a = 3$, $b = 2$.`, r`$a \cdot b = 6$.`],
    errors: [['5', 'Odczytane maksimum zamiast iloczynu parametrów.', r`Z maksimum i minimum wyznacz $a$ i $b$.`]],
  }),
  numeric({
    id: 't-rad-8',
    skill: 'trig-radians',
    kind: 'transfer',
    difficulty: 5,
    prompt: r`Wysokość gondoli diabelskiego młyna opisuje wzór $h(t) = 20 - 18\cos\frac{\pi t}{15}$ (w metrach, $t$ w minutach od startu). Po ilu minutach gondola pierwszy raz znajdzie się na wysokości $29$ m?`,
    answer: 10,
    verify: () => {
      for (let t = 0; t < 30; t += 0.0001) if (20 - 18 * Math.cos((PI * t) / 15) >= 29 - 1e-9) return Math.round(t * 1000) / 1000;
      return NaN;
    },
    tolerance: 0.001,
    hints: ['Jakie równanie trzeba rozwiązać?', r`$20 - 18\cos\frac{\pi t}{15} = 29$.`, r`$\cos\frac{\pi t}{15} = -\frac12$.`, r`Najmniejszy dodatni kąt o cosinusie $-\frac12$ to $\frac{2\pi}{3}$.`],
    steps: [r`$\frac{\pi t}{15} = \frac{2\pi}{3}$.`, r`$t = 10$ minut.`],
    errors: [['5', r`Przyjęte $\cos = \frac12$ zamiast $-\frac12$.`, r`$18\cos\frac{\pi t}{15} = 20 - 29 = -9$.`]],
  }),

  // trig-formulas -------------------------------------------------------------
  numeric({
    id: 't-frm-1',
    skill: 'trig-formulas',
    kind: 'foundation',
    difficulty: 1,
    prompt: r`Wiadomo, że $\sin\alpha = 0{,}6$ i $\cos\alpha = 0{,}8$. Oblicz $\sin 2\alpha$.`,
    answer: 0.96,
    verify: () => 2 * 0.6 * 0.8,
    tolerance: 1e-9,
    hints: ['Jaki jest wzór na sinus podwojonego kąta?', r`$\sin 2\alpha = 2\sin\alpha\cos\alpha$.`, r`$2 \cdot 0{,}6 \cdot 0{,}8$.`, 'Policz.'],
    steps: [r`$2 \cdot 0{,}48$.`, r`$= 0{,}96$.`],
    errors: [['1.2', r`Policzone $2\sin\alpha$.`, r`$\sin 2\alpha = 2\sin\alpha\cos\alpha$, nie $2\sin\alpha$.`]],
  }),
  numeric({
    id: 't-frm-2',
    skill: 'trig-formulas',
    kind: 'foundation',
    difficulty: 2,
    prompt: r`Wiadomo, że $\sin\alpha = 0{,}6$ i $\cos\alpha = 0{,}8$. Oblicz $\cos 2\alpha$.`,
    answer: 0.28,
    verify: () => 0.8 ** 2 - 0.6 ** 2,
    tolerance: 1e-9,
    hints: ['Jaki jest wzór na cosinus podwojonego kąta?', r`$\cos 2\alpha = \cos^2\alpha - \sin^2\alpha$.`, r`$0{,}64 - 0{,}36$.`, 'Odejmij.'],
    steps: [r`$0{,}64 - 0{,}36$.`, r`$= 0{,}28$.`],
    errors: [['-0.28', 'Odwrócona kolejność odejmowania.', r`$\cos 2\alpha = \cos^2\alpha - \sin^2\alpha$.`]],
  }),
  choice({
    id: 't-frm-3',
    skill: 'trig-formulas',
    kind: 'typical',
    difficulty: 2,
    prompt: r`Wyrażenie $\cos^2 15^\circ - \sin^2 15^\circ$ jest równe`,
    choices: [r`$\frac{\sqrt3}{2}$`, r`$\frac12$`, r`$1$`, r`$0$`],
    answer: 'A',
    hints: ['Który wzór ma postać różnicy kwadratów cosinusa i sinusa?', r`$\cos 2\alpha = \cos^2\alpha - \sin^2\alpha$.`, r`$\cos 30^\circ$.`, 'Odczytaj z tabeli.'],
    steps: [r`$\cos^2 15^\circ - \sin^2 15^\circ = \cos 30^\circ$.`, r`$= \frac{\sqrt3}{2}$.`],
    errors: [
      ['B', r`Wzięty $\sin 30^\circ$.`, r`Różnica kwadratów to $\cos 2\alpha$.`],
      ['C', 'Pomylone z jedynką trygonometryczną.', 'Jedynka to SUMA kwadratów.'],
      ['D', 'Uznano, że kwadraty się znoszą.', r`$\cos 15^\circ \ne \sin 15^\circ$.`],
    ],
  }),
  numeric({
    id: 't-frm-4',
    skill: 'trig-formulas',
    kind: 'typical',
    difficulty: 3,
    prompt: r`Oblicz $\sin 15^\circ \cos 15^\circ$.`,
    answer: 0.25,
    variants: ['1/4'],
    verify: () => Math.sin(rad(15)) * Math.cos(rad(15)),
    tolerance: 1e-9,
    hints: ['Który wzór zawiera iloczyn sinusa i cosinusa tego samego kąta?', r`$\sin 2\alpha = 2\sin\alpha\cos\alpha$.`, r`$\sin\alpha\cos\alpha = \frac12\sin 2\alpha$.`, r`$\frac12 \sin 30^\circ$.`],
    steps: [r`$\frac12 \cdot \sin 30^\circ$.`, r`$= \frac14$.`],
    errors: [['0.5', r`Pominięte $\frac12$ przed $\sin 30^\circ$.`, r`$\sin\alpha\cos\alpha = \frac12 \sin 2\alpha$.`]],
  }),
  numeric({
    id: 't-frm-5',
    skill: 'trig-formulas',
    kind: 'typical',
    difficulty: 3,
    prompt: r`Wiadomo, że $\cos\alpha = \frac13$. Oblicz $\cos 2\alpha$.`,
    answer: '-7/9',
    variants: ['-0.778'],
    tolerance: 0.001,
    verify: () => 2 * (1 / 3) ** 2 - 1,
    hints: ['Która postać wzoru na cos 2α używa tylko cosinusa?', r`$\cos 2\alpha = 2\cos^2\alpha - 1$.`, r`$2 \cdot \frac19 - 1$.`, 'Policz.'],
    steps: [r`$\frac29 - 1$.`, r`$= -\frac79$.`],
    errors: [['2/3', r`Policzone $2\cos\alpha$.`, r`$\cos 2\alpha = 2\cos^2\alpha - 1$.`]],
  }),
  numeric({
    id: 't-frm-6',
    skill: 'trig-formulas',
    kind: 'typical',
    difficulty: 4,
    prompt: r`Wiadomo, że $\sin x + \cos x = \frac12$. Oblicz $\sin 2x$.`,
    answer: -0.75,
    variants: ['-3/4'],
    verify: () => 0.5 ** 2 - 1,
    tolerance: 1e-9,
    hints: ['Co otrzymasz, podnosząc obie strony do kwadratu?', r`$\sin^2 x + 2\sin x\cos x + \cos^2 x = \frac14$.`, r`$1 + \sin 2x = \frac14$.`, 'Wyznacz sin 2x.'],
    steps: [r`$\sin 2x = \frac14 - 1$.`, r`$= -\frac34$.`],
    errors: [['-1.5', r`Wynik podzielony przez $\frac12$ zamiast przeniesienia jedynki.`, r`$1 + \sin 2x = \frac14$.`]],
  }),
  numeric({
    id: 't-frm-7',
    skill: 'trig-formulas',
    kind: 'transfer',
    difficulty: 4,
    prompt: r`Wiadomo, że $\mathrm{tg}\,\alpha = 2$. Oblicz $\mathrm{tg}\,2\alpha$.`,
    answer: '-4/3',
    variants: ['-1.333'],
    tolerance: 0.001,
    verify: () => Math.tan(2 * Math.atan(2)),
    hints: ['Jaki jest wzór na tangens podwojonego kąta?', r`$\mathrm{tg}\,2\alpha = \frac{2\,\mathrm{tg}\,\alpha}{1 - \mathrm{tg}^2\alpha}$.`, r`$\frac{2 \cdot 2}{1 - 2^2}$.`, 'Uważaj na znak mianownika.'],
    steps: [r`$\frac{4}{1 - 4} = \frac{4}{-3}$.`, r`$= -\frac43$.`],
    errors: [['4', r`Policzone $2\,\mathrm{tg}\,\alpha$.`, 'Tangens nie jest liniowy — potrzebny wzór.']],
  }),
  numeric({
    id: 't-frm-8',
    skill: 'trig-formulas',
    kind: 'transfer',
    difficulty: 5,
    prompt: r`Ile rozwiązań ma równanie $\cos 2x + \sin x = 0$ w przedziale $\langle 0, 2\pi)$?`,
    answer: 3,
    verify: () => {
      // 1 - 2s^2 + s = 0 -> s = 1 lub s = -1/2
      const sols = new Set<number>();
      for (const s of [1, -0.5]) {
        const a = Math.asin(s);
        for (const x of [a, PI - a]) sols.add(Number((((x % (2 * PI)) + 2 * PI) % (2 * PI)).toFixed(9)));
      }
      return sols.size;
    },
    hints: [r`Którą postać $\cos 2x$ wybrać, żeby została tylko jedna funkcja?`, r`$\cos 2x = 1 - 2\sin^2 x$.`, r`$2\sin^2 x - \sin x - 1 = 0$: $t = \sin x$.`, r`$t = 1$ lub $t = -\frac12$.`],
    steps: [r`$\cos2x=1-2\sin^2x$, więc $2\sin^2x-\sin x-1=0$, czyli $(2\sin x+1)(\sin x-1)=0$.`, r`$\sin x = 1$: $x = \frac{\pi}{2}$; $\sin x = -\frac12$: $x = \frac{7\pi}{6}$ lub $\frac{11\pi}{6}$.`, 'Trzy rozwiązania.'],
    errors: [['2', r`Pominięte jedno z rozwiązań $\sin x = -\frac12$.`, 'Sinus przyjmuje każdą wartość z (−1, 1) dwa razy w okresie.']],
  }),

  // trig-equations (dopisane) -------------------------------------------------
  numeric({
    id: 't-eq-1',
    skill: 'trig-equations',
    kind: 'foundation',
    difficulty: 1,
    prompt: r`Rozwiąż równanie $\sin x = 1$ w przedziale $\langle 0^\circ, 180^\circ \rangle$. Podaj $x$ w stopniach.`,
    answer: 90,
    verify: () => (Math.asin(1) * 180) / PI,
    tolerance: 1e-9,
    hints: ['Dla jakiego kąta sinus osiąga największą wartość?', 'Na okręgu jednostkowym — w najwyższym punkcie.', 'Najwyższy punkt leży na osi y.', 'Jaki kąt tworzy oś y z osią x?'],
    steps: [r`$\sin x = 1$ tylko w najwyższym punkcie okręgu.`, r`$x = 90^\circ$.`],
    errors: [['0', r`Pomylone z $\sin 0^\circ$.`, r`$\sin 0^\circ = 0$.`]],
  }),
  numeric({
    id: 't-eq-2',
    skill: 'trig-equations',
    kind: 'foundation',
    difficulty: 2,
    prompt: r`Rozwiąż równanie $\cos x = -\frac12$ w przedziale $\langle 0^\circ, 180^\circ \rangle$. Podaj $x$ w stopniach.`,
    answer: 120,
    verify: () => (Math.acos(-0.5) * 180) / PI,
    tolerance: 1e-9,
    hints: ['Jaki znak ma cosinus kąta rozwartego?', 'Ujemny — szukany kąt jest rozwarty.', r`$\cos(180^\circ - \alpha) = -\cos\alpha$, a $\cos 60^\circ = \frac12$.`, r`$180^\circ - 60^\circ$.`],
    steps: [r`$\cos x = -\cos 60^\circ = \cos 120^\circ$.`, r`$x = 120^\circ$.`],
    errors: [['60', 'Zgubiony minus.', r`$\cos 60^\circ = +\frac12$.`]],
  }),
  numeric({
    id: 't-eq-3',
    skill: 'trig-equations',
    kind: 'typical',
    difficulty: 4,
    prompt: r`Ile rozwiązań ma równanie $2\sin^2 x - \sin x = 0$ w przedziale $\langle 0^\circ, 360^\circ \rangle$?`,
    answer: 5,
    verify: () => [0, 180, 360, 30, 150].filter((x) => Math.abs(2 * Math.sin(rad(x)) ** 2 - Math.sin(rad(x))) < 1e-9).length,
    hints: ['Co można wyłączyć przed nawias?', r`$\sin x\,(2\sin x - 1) = 0$.`, r`$\sin x = 0$ lub $\sin x = \frac12$.`, 'Wypisz rozwiązania każdego z przedziału — uwaga na oba końce.'],
    steps: [r`$\sin x = 0$: $0^\circ, 180^\circ, 360^\circ$; $\sin x = \frac12$: $30^\circ, 150^\circ$.`, 'Pięć rozwiązań.'],
    errors: [['4', r`Pominięty koniec przedziału $360^\circ$.`, 'Przedział jest domknięty z obu stron.']],
  }),
  numeric({
    id: 't-eq-4',
    skill: 'trig-equations',
    kind: 'transfer',
    difficulty: 5,
    prompt: r`Rozwiąż równanie $\sin 2x = \cos x$ w przedziale $\langle 0^\circ, 180^\circ \rangle$. Podaj sumę rozwiązań w stopniach.`,
    answer: 270,
    verify: () => [30, 90, 150].filter((x) => Math.abs(Math.sin(rad(2 * x)) - Math.cos(rad(x))) < 1e-9).reduce((a, b) => a + b, 0),
    hints: [r`Jak zapisać $\sin 2x$, żeby pojawił się wspólny czynnik?`, r`$2\sin x\cos x - \cos x = 0$.`, r`$\cos x\,(2\sin x - 1) = 0$ — nie dziel przez $\cos x$!`, r`$\cos x = 0$ lub $\sin x = \frac12$.`],
    steps: [r`$2\sin x\cos x=\cos x$ daje $\cos x(2\sin x-1)=0$. Nie dzielimy przez $\cos x$, bo może być zerem.`, r`$\cos x=0$ daje $x=90^\circ$; $\sin x=1/2$ daje $x=30^\circ$ lub $150^\circ$ w zadanym przedziale.`, r`Suma: $270^\circ$.`],
    errors: [['180', r`Podzielono przez $\cos x$ — zgubione $x = 90^\circ$.`, 'Nie dziel przez wyrażenie, które może być zerem.']],
  }),
];

export const TRIG_COURSE_QUESTIONS: Question[] = [...TRIG_QUESTIONS, ...NEW_QUESTIONS];

// ===========================================================================
// Fiszki
// ===========================================================================

export const TRIG_CARDS: Flashcard[] = [
  card('c-trig-v-1', 'trig-values', 'definicja', 'Sinus, cosinus, tangens kąta ostrego?', 'sin: naprzeciw/przeciwprostokątna; cos: przy/przeciwprostokątna; tg: naprzeciw/przy.'),
  card('c-trig-v-2', 'trig-values', 'wzor', r`$\sin 30^\circ$, $\sin 45^\circ$, $\sin 60^\circ$?`, r`$\frac12$, $\frac{\sqrt2}{2}$, $\frac{\sqrt3}{2}$ (cosinusy odwrotnie).`),

  card('c-trig-i-1', 'trig-identities', 'wzor', 'Jedynka trygonometryczna i tangens?', r`$\sin^2\alpha + \cos^2\alpha = 1$, $\mathrm{tg}\,\alpha = \frac{\sin\alpha}{\cos\alpha}$`),
  card('c-trig-i-2', 'trig-identities', 'metoda', r`Znasz $\mathrm{tg}\,\alpha$ — jak policzyć sinus i cosinus?`, 'Narysuj trójkąt o takich przyprostokątnych albo wstaw sin = tg·cos do jedynki.'),

  card('c-trig-o-1', 'trig-obtuse', 'wzor', r`$\sin(180^\circ - \alpha)$ i $\cos(180^\circ - \alpha)$?`, r`$\sin\alpha$ oraz $-\cos\alpha$.`),
  card('c-trig-o-2', 'trig-obtuse', 'wzor', 'Kąt nachylenia prostej y = ax + b?', r`$\mathrm{tg}\,\alpha = a$`),

  card('c-trig-a-1', 'trig-area', 'wzor', 'Pole trójkąta z dwóch boków i kąta?', r`$P = \frac12 ab\sin\gamma$ — kąt MIĘDZY bokami.`),
  card('c-trig-a-2', 'trig-area', 'pulapka', r`$\sin\gamma = \frac12$ w trójkącie — jaki kąt?`, r`$30^\circ$ albo $150^\circ$ — sprawdź treść.`),

  card('c-trig-l-1', 'trig-laws', 'wzor', 'Twierdzenie cosinusów?', r`$c^2 = a^2 + b^2 - 2ab\cos\gamma$`),
  card('c-trig-l-2', 'trig-laws', 'wzor', 'Twierdzenie sinusów?', r`$\frac{a}{\sin\alpha} = \frac{b}{\sin\beta} = \frac{c}{\sin\gamma} = 2R$`),

  card('c-trig-r-1', 'trig-radians', 'wzor', 'Stopnie ↔ radiany?', r`$180^\circ = \pi$`),
  card('c-trig-r-2', 'trig-radians', 'definicja', r`Okres $\sin x$, $\cos x$, $\mathrm{tg}\,x$?`, r`$2\pi$, $2\pi$, $\pi$`),

  card('c-trig-f-1', 'trig-formulas', 'wzor', r`$\sin 2\alpha$ i $\cos 2\alpha$?`, r`$2\sin\alpha\cos\alpha$; $\cos^2\alpha - \sin^2\alpha = 2\cos^2\alpha - 1 = 1 - 2\sin^2\alpha$`),
  card('c-trig-f-2', 'trig-formulas', 'pulapka', r`$\sin 2\alpha = 2\sin\alpha$?`, 'NIE. Sinus nie jest liniowy.'),

  card('c-trig-e-1', 'trig-equations', 'metoda', r`$\sin x = \sin a$ — wszystkie rozwiązania?`, r`$x = a + 2k\pi$ lub $x = \pi - a + 2k\pi$.`),
  card('c-trig-e-2', 'trig-equations', 'pulapka', r`Czy wolno podzielić równanie przez $\cos x$?`, r`Tak, ale tylko dla $\cos x\ne0$. Najpierw osobno sprawdź przypadek $\cos x=0$, aby nie zgubić rozwiązań. Gdy to możliwe, bezpieczniej wyłączyć wspólny czynnik przed nawias.`),
];
